import { useMemo, useState } from "react";
import Avatar from "@/components/base/Avatar";
import type { DiscussionItem } from "@/types/content";

interface TopicDiscussionProps {
  topicTitle: string;
  initialItems: DiscussionItem[];
}

const tones = ["primary", "accent", "secondary"] as const;

export default function TopicDiscussion({ topicTitle, initialItems }: TopicDiscussionProps) {
  const [items, setItems] = useState<DiscussionItem[]>(initialItems);
  const [draft, setDraft] = useState("");
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [notice, setNotice] = useState("");
  const [sort, setSort] = useState<"newest" | "hotest">("newest");

  const sorted = useMemo(() => {
    const base = [...items];
    if (sort === "hotest") return base.sort((a, b) => b.likes - a.likes);
    return base;
  }, [items, sort]);

  const handleLike = (id: string) => {
    setLiked((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSubmit = () => {
    const text = draft.trim();
    if (!text) {
      setNotice("先写点什么再发表哦");
      return;
    }
    if (text.length > 300) {
      setNotice("观点请控制在 300 字以内");
      return;
    }
    const newItem: DiscussionItem = {
      id: `local-${Date.now()}`,
      user: "我",
      handle: "@me",
      time: "刚刚",
      text,
      likes: 0,
      badge: "新观点",
    };
    setItems((prev) => [newItem, ...prev]);
    setDraft("");
    setNotice("已发表，观点已加入讨论区");
    window.setTimeout(() => setNotice(""), 2600);
  };

  return (
    <section className="mt-10" aria-labelledby="discussion-title">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2
          id="discussion-title"
          className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950"
        >
          <span className="flex h-5 w-5 items-center justify-center text-primary-600">
            <i className="ri-chat-3-line text-lg" />
          </span>
          围观群众讨论区
          <span className="text-sm font-normal text-foreground-500">({items.length})</span>
        </h2>
        <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setSort("newest")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
              sort === "newest" ? "bg-primary-500 text-background-50" : "text-foreground-600"
            }`}
          >
            最新
          </button>
          <button
            type="button"
            onClick={() => setSort("hotest")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
              sort === "hotest" ? "bg-primary-500 text-background-50" : "text-foreground-600"
            }`}
          >
            最赞
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-card border border-background-200 bg-background-100 p-4 md:p-5">
        <div className="flex items-start gap-3">
          <Avatar name="我" tone="accent" />
          <div className="flex-1">
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={3}
              maxLength={300}
              placeholder={`关于「${topicTitle.slice(0, 16)}」，说说你的看法…`}
              className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <span className="flex items-center gap-3 text-xs text-foreground-500">
                <span>{draft.length}/300</span>
                <span className="flex items-center gap-1">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-emotion-happy-line text-sm" />
                  </span>
                  理性发言，友善围观
                </span>
              </span>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-send-plane-fill text-sm" />
                </span>
                发表观点
              </button>
            </div>
            {notice && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-accent-700">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-checkbox-circle-line text-sm" />
                </span>
                {notice}
              </p>
            )}
          </div>
        </div>
      </div>

      <ul className="mt-5 flex flex-col gap-3">
        {sorted.map((item, index) => {
          const isLiked = liked[item.id];
          return (
            <li
              key={item.id}
              className="rounded-card border border-background-200 bg-background-50 p-4 md:p-5"
            >
              <div className="flex items-start gap-3">
                <Avatar name={item.user} tone={tones[index % tones.length]} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-foreground-900">{item.user}</span>
                    <span className="text-xs text-foreground-400">{item.handle}</span>
                    {item.badge && (
                      <span className="rounded-md bg-secondary-100 px-1.5 py-0.5 text-[10px] font-medium text-secondary-800">
                        {item.badge}
                      </span>
                    )}
                    <span className="text-xs text-foreground-400">{item.time}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => handleLike(item.id)}
                      className={`flex cursor-pointer items-center gap-1.5 text-xs transition-colors ${
                        isLiked ? "text-primary-600" : "text-foreground-500 hover:text-primary-600"
                      }`}
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className={isLiked ? "ri-thumb-up-fill text-base" : "ri-thumb-up-line text-base"} />
                      </span>
                      {item.likes + (isLiked ? 1 : 0)}
                    </button>
                    <button
                      type="button"
                      className="flex cursor-pointer items-center gap-1.5 text-xs text-foreground-500 transition-colors hover:text-primary-600"
                    >
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-message-2-line text-base" />
                      </span>
                      回复
                    </button>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}